import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "lucide-react";
import Sidebar from "./sidebar";
import Topbar from "./topbar";
import ErrorBoundary from "./error-boundary";
import HelpDialog from "./help-dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ToastProvider } from "@/components/ui/toast";
import { authApi } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import AiAssistant from "@/components/ai-assistant";

// Dev-only escape hatch: setting `localStorage.luzgrid:dev-mock = "1"` (or
// loading the app with `?dev-mock=1`) skips the auth probe so the new shell
// can be inspected without a backend. Production builds ignore this.
function isDevMock() {
  if (typeof window === "undefined") return false;
  if (!import.meta.env.DEV) return false;
  if (new URLSearchParams(window.location.search).get("dev-mock") === "1") {
    try { localStorage.setItem("luzgrid:dev-mock", "1"); } catch { /* ignore */ }
    return true;
  }
  try { return localStorage.getItem("luzgrid:dev-mock") === "1"; } catch { return false; }
}

export default function AppShell() {
  const devMock = isDevMock();
  // Cheap session probe. The api.ts request() helper auto-redirects on 401,
  // so a successful return here means the cookie is good.
  const { isLoading, isError } = useQuery({
    queryKey: ["auth", "ping"],
    queryFn: authApi.ping,
    staleTime: 5 * 60_000,
    retry: false,
    enabled: !devMock,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // ? opens help dialog (force = true so it works even when an input is
  // focused — small UX win since you might be mid-typing and forget a chord)
  useKeyboardShortcuts(
    [{ key: "?", shift: true, force: true, handler: () => setHelpOpen(true) }],
    true,
  );

  if (!devMock && isLoading) return <BootSplash />;
  if (!devMock && isError) return <AuthFailedSplash />;

  return (
    <ToastProvider>
      <div className="flex h-screen w-screen overflow-hidden bg-bg-app">
        {/* Desktop sidebar */}
        <div className="hidden md:block">
          <Sidebar />
        </div>

        {/* Mobile drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" hideClose className="border-0 p-0">
            <Sidebar />
          </SheetContent>
        </Sheet>

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar onOpenDrawer={() => setDrawerOpen(true)} onOpenHelp={() => setHelpOpen(true)} />
          <main className="min-h-0 flex-1 overflow-hidden">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>

        <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
        {/* Lumen — inline ✦ buttons live next to text fields (per
            MASTER_PROMPT §2.4); the side-panel-style assistant remains as the
            opt-in conversational surface. The legacy FAB is kept only as the
            launcher for the side panel and is re-skinned subtly so it reads
            as an unobtrusive chip, not a marketing-style purple gradient. */}
        <AiAssistant />
      </div>
    </ToastProvider>
  );
}

function MenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded p-1.5 text-text-2 hover:bg-bg-hover hover:text-text md:hidden"
      aria-label="Open menu"
    >
      <Menu className="h-5 w-5" />
    </button>
  );
}
export { MenuButton };

function BootSplash() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-bg-app">
      <div className="flex flex-col items-center gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-lg bg-accent text-accent-fg">
          <span className="font-display text-base font-bold">LG</span>
        </div>
        <Skeleton className="h-2 w-32" />
      </div>
    </div>
  );
}

function AuthFailedSplash() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-bg-app">
      <div className="text-center">
        <p className="font-display text-2xl font-semibold text-text">Sign in to continue</p>
        <p className="mt-1 text-sm text-text-2">Redirecting you to Microsoft 365…</p>
      </div>
    </div>
  );
}
